import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, Eye, EyeOff, Loader2 } from 'lucide-react'

import { api } from '@/api/client'
import { Modal } from '@/components/Modal'
import { inputCls } from '@/components/Field'
import { confirm } from '@/stores/confirm'
import { useToast } from '@/stores/toast'
import { cn, formatNum } from '@/lib/utils'

interface GroupRow {
  id: number; name: string; company_id: number; company_name: string
  members: number; units: number; hidden: number; unassigned: number
}
interface UnitRow {
  label: string; rollup_key: string; is_group_fallback: boolean
  partner_ids: number[]; members: number; hidden: boolean
}
interface HiddenRow extends UnitRow { company_name: string; group_id: number | null; group_name: string }
interface PartnerRow {
  id: number; code: string; name: string; rollup_name: string
  unit_label: string; hidden: boolean; month_sales: string | number
}
interface GroupDetail {
  group: { id: number; name: string; company_id: number }
  month: string; units: UnitRow[]; partners: PartnerRow[]
}

const NEW = '__new__'
const HIDE_WARN = '숨긴 줄은 일일매출 목록과 합계에서 모두 빠집니다. 숨길까요?'

/** 일일매출 > 표시 설정 — 상위거래처별로 업장을 어느 줄(표기단위)에 묶을지, 줄 숨김 관리. 영구 저장. */
export function SalesDisplaySettings({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient()
  const push = useToast((s) => s.push)
  const [q, setQ] = useState('')
  const [onlyIssues, setOnlyIssues] = useState(false)
  const [gid, setGid] = useState<number | null>(null)
  const [newName, setNewName] = useState<Record<number, string>>({})
  const [newOpen, setNewOpen] = useState<Record<number, boolean>>({})

  const groupsQ = useQuery({
    queryKey: ['sales-settings-groups'], enabled: open,
    queryFn: () => api<GroupRow[]>('/api/sales/settings/groups'),
  })
  const hiddenQ = useQuery({
    queryKey: ['sales-settings-hidden'], enabled: open,
    queryFn: () => api<HiddenRow[]>('/api/sales/settings/hidden'),
  })
  const detailQ = useQuery({
    queryKey: ['sales-settings-group', gid], enabled: open && gid != null,
    queryFn: () => api<GroupDetail>(`/api/sales/settings/groups/${gid}`),
  })

  const refresh = () => {
    for (const k of ['sales-daily', 'sales-settings-groups', 'sales-settings-hidden', 'sales-settings-group']) {
      qc.invalidateQueries({ queryKey: [k] })
    }
  }
  const setRollup = useMutation({
    mutationFn: (v: { partner_id: number; rollup_name: string }) =>
      api('/api/sales/settings/rollup', { method: 'POST', body: v }),
    onSuccess: (_, v) => {
      refresh()
      setNewOpen((s) => ({ ...s, [v.partner_id]: false }))
      push('저장됨', 'success')
    },
    onError: (e) => push(`저장 실패: ${e.message}`, 'error', 6000),
  })
  const hide = useMutation({
    mutationFn: (v: { partners: number[]; hidden: boolean }) =>
      api('/api/sales/hide', { method: 'POST', body: v }),
    onSuccess: () => refresh(),
    onError: (e) => push(`실패: ${e.message}`, 'error', 6000),
  })
  async function toggleHide(u: { partner_ids: number[]; hidden: boolean }) {
    if (!u.hidden && !(await confirm(HIDE_WARN, { danger: true }))) return
    hide.mutate({ partners: u.partner_ids, hidden: !u.hidden })
  }

  const groups = groupsQ.data ?? []
  const shownGroups = useMemo(() => {
    const t = q.trim().toLowerCase()
    return groups.filter((g) =>
      (!onlyIssues || g.hidden > 0 || g.unassigned > 0) &&
      (!t || g.name.toLowerCase().includes(t) || g.company_name.toLowerCase().includes(t)))
  }, [groups, q, onlyIssues])

  const d = detailQ.data
  const namedUnits = (d?.units ?? []).filter((u) => !u.is_group_fallback).map((u) => u.label)
  const hasNamed = namedUnits.length > 0

  return (
    <Modal open={open} onClose={onClose} title="일일매출 표시 설정" size="5xl">
      <div className="flex gap-4 min-h-[560px]">
        {/* 왼쪽: 상위거래처 선택 */}
        <div className="w-72 shrink-0 flex flex-col">
          <input value={q} onChange={(e) => setQ(e.target.value)}
                 placeholder="상위거래처 · 회사 검색" className={inputCls}/>
          <label className="flex items-center gap-1.5 text-xs text-slate-600 mt-2 mb-1">
            <input type="checkbox" checked={onlyIssues} onChange={(e) => setOnlyIssues(e.target.checked)}/>
            숨김·줄 미지정 있는 것만
          </label>
          <button onClick={() => setGid(null)}
                  className={cn('text-left text-sm rounded-lg px-2 py-1.5 mb-1 border',
                    gid == null ? 'border-amber-400 bg-amber-50 text-amber-800' : 'border-slate-200 text-slate-600 hover:bg-slate-50')}>
            숨긴 줄 전체 보기 ({hiddenQ.data?.length ?? '…'})
          </button>
          <div className="flex-1 overflow-auto border border-slate-200 rounded-lg max-h-[500px]">
            {groupsQ.isLoading && <div className="p-3 text-xs text-slate-400">불러오는 중…</div>}
            {shownGroups.map((g) => (
              <button key={g.id} onClick={() => setGid(g.id)}
                      className={cn('w-full text-left px-2.5 py-1.5 border-b border-slate-100 text-sm',
                        gid === g.id ? 'bg-emerald-50' : 'hover:bg-slate-50')}>
                <div className="flex items-center gap-1">
                  <span className="font-medium text-slate-800 truncate">{g.name}</span>
                  <span className="text-[11px] text-slate-400 shrink-0">({g.members})</span>
                  {g.hidden > 0 && <span className="ml-auto text-[10px] text-amber-700 border border-amber-300 rounded px-1 shrink-0">숨김 {g.hidden}</span>}
                  {g.unassigned > 0 && <span className={cn('text-[10px] text-rose-700 border border-rose-300 rounded px-1 shrink-0', !g.hidden && 'ml-auto')}>미지정 {g.unassigned}</span>}
                </div>
                <div className="text-[11px] text-slate-400">{g.company_name}</div>
              </button>
            ))}
          </div>
        </div>

        {/* 오른쪽 */}
        <div className="flex-1 min-w-0">
          {gid == null ? (
            <div>
              <h4 className="text-sm font-semibold text-slate-800 mb-1">숨긴 줄 (전 회사)</h4>
              <p className="text-xs text-slate-500 mb-3">숨긴 줄은 일일매출 목록과 합계에서 빠집니다. 필요 없으면 숨김을 해제하세요.</p>
              <table className="w-full text-sm border border-slate-200 rounded-lg overflow-hidden">
                <thead className="bg-slate-50 text-xs text-slate-500">
                  <tr><th className="px-3 py-2 text-left">회사</th><th className="px-3 py-2 text-left">상위거래처</th>
                    <th className="px-3 py-2 text-left">줄</th><th className="px-3 py-2 text-right w-16">업장</th><th className="w-28"/></tr>
                </thead>
                <tbody>
                  {(hiddenQ.data ?? []).map((h) => (
                    <tr key={h.company_name + h.rollup_key} className="border-t border-slate-100">
                      <td className="px-3 py-1.5 text-slate-600">{h.company_name}</td>
                      <td className="px-3 py-1.5 text-slate-600">
                        {h.group_id ? <button className="hover:underline" onClick={() => setGid(h.group_id)}>{h.group_name}</button> : '—'}
                      </td>
                      <td className="px-3 py-1.5 font-medium">{h.label}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{h.members}</td>
                      <td className="px-3 py-1.5 text-right">
                        <button onClick={() => toggleHide(h)} disabled={hide.isPending}
                                className="text-xs inline-flex items-center gap-1 text-emerald-700 hover:text-emerald-900 disabled:opacity-40">
                          <Eye className="w-3.5 h-3.5"/> 숨김 해제
                        </button>
                      </td>
                    </tr>
                  ))}
                  {hiddenQ.data && !hiddenQ.data.length && (
                    <tr><td colSpan={5} className="text-center py-8 text-slate-400 text-sm">숨긴 줄이 없습니다.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          ) : !d ? (
            <div className="p-6 text-slate-400 text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin"/> 불러오는 중…</div>
          ) : (
            <div>
              <h4 className="text-sm font-semibold text-slate-800 mb-2">{d.group.name} — 일일매출 줄</h4>
              <table className="w-full text-sm border border-slate-200 mb-4">
                <thead className="bg-slate-50 text-xs text-slate-500">
                  <tr><th className="px-3 py-2 text-left">줄 이름</th><th className="px-3 py-2 text-right w-16">업장</th>
                    <th className="px-3 py-2 text-center w-28">숨김</th></tr>
                </thead>
                <tbody>
                  {d.units.map((u) => (
                    <tr key={u.rollup_key} className={cn('border-t border-slate-100', u.hidden && 'bg-amber-50/60')}>
                      <td className="px-3 py-1.5">
                        <span className="font-medium">{u.label}</span>
                        {u.is_group_fallback && hasNamed && (
                          <span className="ml-2 text-[11px] text-rose-700 inline-flex items-center gap-0.5">
                            <AlertTriangle className="w-3 h-3"/> 줄 미지정 업장(상위거래처 이름으로 따로 표시됨)
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{u.members}</td>
                      <td className="px-3 py-1.5 text-center">
                        <button onClick={() => toggleHide(u)} disabled={hide.isPending}
                                className={cn('text-xs inline-flex items-center gap-1 disabled:opacity-40',
                                  u.hidden ? 'text-amber-700' : 'text-slate-400 hover:text-slate-700')}>
                          {u.hidden ? <><EyeOff className="w-3.5 h-3.5"/> 숨김 (해제)</> : <><Eye className="w-3.5 h-3.5"/> 표시 중</>}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <h4 className="text-sm font-semibold text-slate-800 mb-1">소속 업장 ({d.partners.length})</h4>
              <p className="text-xs text-slate-500 mb-2">업장별로 일일매출에서 묶일 줄을 고르면 바로 저장되고 이후 계속 적용됩니다. ({d.month} 매출 표시)</p>
              <div className="max-h-[330px] overflow-auto border border-slate-200">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-xs text-slate-500 sticky top-0">
                    <tr><th className="px-3 py-2 text-left">업장</th><th className="px-3 py-2 text-right w-28">이번 달</th>
                      <th className="px-3 py-2 text-left w-72">줄</th></tr>
                  </thead>
                  <tbody>
                    {d.partners.map((p) => {
                      const unassigned = !p.rollup_name && hasNamed
                      const value = newOpen[p.id] ? NEW : p.rollup_name
                      return (
                        <tr key={p.id} className={cn('border-t border-slate-100', unassigned && 'bg-rose-50')}>
                          <td className="px-3 py-1.5">
                            <span className="font-mono text-[11px] text-slate-400 mr-1">{p.code}</span>{p.name}
                            {p.hidden && <span className="ml-1 text-[10px] text-amber-700 border border-amber-300 rounded px-1">숨김</span>}
                          </td>
                          <td className="px-3 py-1.5 text-right tabular-nums text-slate-600">
                            {Number(p.month_sales) ? formatNum(p.month_sales) : '—'}
                          </td>
                          <td className="px-3 py-1.5">
                            <div className="flex items-center gap-1">
                              <select value={value} disabled={setRollup.isPending}
                                      onChange={(e) => {
                                        const v = e.target.value
                                        if (v === NEW) { setNewOpen((s) => ({ ...s, [p.id]: true })); return }
                                        setNewOpen((s) => ({ ...s, [p.id]: false }))
                                        setRollup.mutate({ partner_id: p.id, rollup_name: v })
                                      }}
                                      className={inputCls + ' !py-1 text-xs'}>
                                <option value="">{d.group.name} (상위거래처 이름)</option>
                                {namedUnits.filter((n) => n !== d.group.name).map((n) => <option key={n} value={n}>{n}</option>)}
                                <option value={NEW}>+ 새 줄 만들기…</option>
                              </select>
                            </div>
                            {newOpen[p.id] && (
                              <div className="flex items-center gap-1 mt-1">
                                <input autoFocus value={newName[p.id] ?? ''} placeholder="새 줄 이름"
                                       onChange={(e) => setNewName((s) => ({ ...s, [p.id]: e.target.value }))}
                                       onKeyDown={(e) => {
                                         if (e.key === 'Enter' && (newName[p.id] ?? '').trim())
                                           setRollup.mutate({ partner_id: p.id, rollup_name: newName[p.id].trim() })
                                       }}
                                       className={inputCls + ' !py-1 text-xs'}/>
                                <button disabled={!(newName[p.id] ?? '').trim() || setRollup.isPending}
                                        onClick={() => setRollup.mutate({ partner_id: p.id, rollup_name: newName[p.id].trim() })}
                                        className="text-xs bg-emerald-600 text-white rounded px-2 py-1 disabled:opacity-40">저장</button>
                                <button onClick={() => setNewOpen((s) => ({ ...s, [p.id]: false }))}
                                        className="text-xs text-slate-500 px-1">취소</button>
                              </div>
                            )}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </Modal>
  )
}
